import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setServicesOpen(false);
  }, [location]);

  const services = [
    { name: 'Private Charter', path: '/services/private-charter' },
    { name: 'Group Charter', path: '/services/group-charter' },
    { name: 'Air Cargo', path: '/services/air-cargo' },
    { name: 'Special Missions', path: '/services/special-missions' },
    { name: 'On-Demand Fleet', path: '/fleet' },
  ];

  const navLinks = [
    { name: 'Private Charter', path: '/services/private-charter' },
    { name: 'Group Charter', path: '/services/group-charter' },
    { name: 'About Us', path: '/about' },
    { name: 'Singapore', path: '/singapore' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="container-padding">
        <div className="flex items-center justify-between h-24 md:h-28">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={isScrolled 
                ? "https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/fjkzy2pa_SACLogo-01.png" 
                : "https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/au4hsghu_SACLogo-02.png"}
              alt="Singapore Air Charter"
              className="h-24 md:h-28 lg:h-32 w-auto"
            />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  isScrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'
                } ${location.pathname === link.path ? 'text-[#C5A059]' : ''}`}
              >
                {link.name}
              </Link>
            ))}

            {/* Services Dropdown */}
            <div className="relative">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`flex items-center gap-1 text-sm font-medium tracking-wide ${
                  isScrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg border border-slate-100 py-2"
                  >
                    {services.map((service) => (
                      <Link
                        key={service.name}
                        to={service.path}
                        className="block px-4 py-3 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              className="bg-red-600 text-white px-6 py-3 text-sm font-medium uppercase hover:bg-red-700 transition"
            >
              Charter Enquiry
            </Link>
          </div>

          {/* Mobile Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 ${isScrolled ? 'text-slate-900' : 'text-white'}`}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100"
          >
            <div className="container-padding py-6 space-y-4">
              
              {/* Main Links */}
              {navLinks.map((link) => (
                <Link key={link.name} to={link.path} className="block py-2 text-slate-700">
                  {link.name}
                </Link>
              ))}

              {/* ✅ Services Section（新增） */}
              <div className="pt-4 border-t">
                <p className="text-xs uppercase text-slate-400 mb-2">Services</p>

                <div className="flex flex-col space-y-2">
                  {services.map((service) => (
                    <Link
                      key={service.name}
                      to={service.path}
                      className="block py-2 text-slate-700"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div className="pt-4 border-t space-y-4">
                <a href="tel:+6581888023" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  +65 8188 8023
                </a>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
